import { Button, Grid, PopUp, ProgressBar } from 'geniebook-ui/dist';
import React from 'react';
import { BadgeItem, Level } from '@models/interfaces/student.interface';
import BadgeBanner from '@components/student/badges/BadgesBanner';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { useGetBadges } from '@hooks/student.hook';
import CrownGrey from '@assets/icons/crown-grey';
import CrownYellow from '@assets/icons/crown-yellow';
import { useAtomValue } from 'jotai';
import { userDetailQueryAtom } from '@atom/globalQueryAtom';
import { useIsJCUser } from '@hooks/user.hook';
import { useNavigate } from 'react-router-dom';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');
const BADGE_MAX_LEVEL = 4;
const BADGE_GENEROUS_ID = 17;

type ButtonGroupProps = {
  hideModal: () => void;
  setModalProps: (params: BadgeItem | null) => void;
};

const ButtonGroup = ({ hideModal, setModalProps }: ButtonGroupProps) => {
  return (
    <div className={'w-full'}>
      <Button
        className="max-w-[100%] w-full"
        onClick={() => {
          hideModal();
          setModalProps(null);
        }}
      >
        Got it
      </Button>
    </div>
  );
};

const BadgesContent = () => {
  const queryGetDetail = useAtomValue(userDetailQueryAtom);
  const navigate = useNavigate();
  const { isJC } = useIsJCUser({
    subjectId: undefined,
    generalPage: true,
  });

  if (isJC) {
    navigate('/profile');
  }

  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [modalProps, setModalProps] = React.useState<BadgeItem | null>(null);

  const viewWidth = useViewWidth();

  const getBadgesQuery = useGetBadges();

  const handleShowModal = () => {
    setShowModal(true);
  };

  const hideModal = () => {
    setShowModal(false);
  };

  const replaceX = (text: string, x: string) => {
    return text.replace('{x}', x);
  };

  const getModalBody = (props: BadgeItem) => {
    const current_level = props.current_level;
    const max_progress = props.levels[current_level - 1].max_progress;
    const current_progress = props.levels[current_level - 1].current_progress;

    const calculateBadgeProgress = (): number => {
      if (props.badge_id === BADGE_GENEROUS_ID) {
        if (
          Number(current_level) === BADGE_MAX_LEVEL &&
          current_progress === max_progress
        ) {
          return 1;
        } else {
          return 0;
        }
      } else {
        return current_progress;
      }
    };

    let badge_description =
      current_level == BADGE_MAX_LEVEL &&
      current_progress == max_progress &&
      props.levels[current_level - 1].unlocked_date !== null
        ? "You've climbed to the max level,<br> you're fantastic!"
        : props.badge_description +
          ' to reach level ' +
          (current_level == 1 &&
          props.levels[current_level - 1].unlocked_date == null
            ? 1
            : current_level);

    return (
      <>
        <div
          className="mb-4"
          dangerouslySetInnerHTML={{
            __html: replaceX(badge_description, max_progress.toString()),
          }}
        ></div>

        <>
          <ProgressBar
            backgroundColor={
              modalProps?.levels[0].current_progress ===
              modalProps?.levels[0].max_progress
                ? 'bg-primary-apricot-light'
                : 'bg-neutral-periwinkle'
            }
            height="10px"
            progressColor="bg-primary-apricot"
            width={
              (calculateBadgeProgress() /
                (props.badge_id === BADGE_GENEROUS_ID ? 1 : max_progress)) *
              100
            }
          />

          <div className="mt-2 text-center">
            {calculateBadgeProgress()}/
            {props.badge_id == BADGE_GENEROUS_ID ? 1 : max_progress}
          </div>
        </>
      </>
    );
  };

  return (
    <>
      {/* Content */}
      <div className="mx-auto ">
        {/* Page title */}
        <div className="text-neutral-whiteboard text-lg mb-4 font-semibold max-lg:hidden">
          Badges
        </div>

        <BadgeBanner />

        {/* Badges Grid */}
        {!getBadgesQuery.error && (
          <Grid className="mt-4 gap-x-4 gap-y-[31px] grid max-md:grid-cols-3 md:grid-cols-4">
            <>
              {getBadgesQuery.data?.data?.data &&
                getBadgesQuery.data?.data?.data?.length > 0 &&
                getBadgesQuery.data?.data.data.map((badge: BadgeItem) => (
                  <Grid.Item key={`badge-${badge.badge_id}`} colSpan={1}>
                    <div
                      className="text-center font-bold cursor-pointer"
                      onClick={() => {
                        handleShowModal();
                        setModalProps(badge);
                      }}
                    >
                      <div className="text-center">
                        {badge.levels.length > 0 &&
                          badge.levels.map((level: Level) => {
                            if (level.level == 1) {
                              let badge_icon =
                                level.unlocked_date == null
                                  ? badge.locked_icon
                                  : level.icon;

                              return (
                                <img
                                  className="inline-block"
                                  src={badge_icon}
                                  alt={level.badge_name}
                                  key={level.badge_level_id}
                                />
                              );
                            }
                          })}
                      </div>

                      <div className="mt-2 max-md:text-[12px] md:text-md">
                        {badge.levels[0].badge_name}
                      </div>
                      {queryGetDetail.isSuccess && (
                        <div
                          className={`mt-2 ${
                            badge.levels[0].unlocked_date == null
                              ? 'text-[#B4B4B4]'
                              : 'text-[#FED00B]'
                          } max-sm:text-[10px] text-[12px] font-black`}
                        >
                          {badge.levels[0].unlocked_date == null ? (
                            <CrownGrey
                              className="h-3.5 mt-[-3px] inline-block mr-2 w-3.5"
                              height={12}
                            />
                          ) : (
                            <CrownYellow
                              className="h-3.5 mt-[-3px] inline-block mr-2 w-3.5"
                              height={12}
                            />
                          )}
                          Level{' '}
                          {badge.levels[0].unlocked_date == null
                            ? 0
                            : badge.current_level == BADGE_MAX_LEVEL &&
                              badge.levels[BADGE_MAX_LEVEL - 1]
                                .current_progress ==
                                badge.levels[BADGE_MAX_LEVEL - 1].max_progress
                            ? BADGE_MAX_LEVEL
                            : badge.current_level == 1
                            ? 1
                            : badge.current_level - 1}
                        </div>
                      )}
                    </div>
                  </Grid.Item>
                ))}
            </>
          </Grid>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <PopUp
          img={
            modalProps?.current_level ||
            0 > 1 ||
            modalProps?.levels[0].current_progress ===
              modalProps?.levels[0].max_progress
              ? modalProps?.levels[0].icon
              : modalProps?.locked_icon
          }
          buttonGroup={
            <ButtonGroup hideModal={hideModal} setModalProps={setModalProps} />
          }
          onClose={hideModal}
          width={viewWidth < breakpoint.md ? `${viewWidth - 30}px` : '400px'}
          title={modalProps ? modalProps.levels[0].badge_name : 'Badge'}
          description={modalProps !== null && getModalBody(modalProps)}
          disableBodyScroll={true}
        />
      )}
    </>
  );
};

export default BadgesContent;
